// Deadlines on a matter — PI Platform outline v2 §3.11.
//
// The cross-matter Deadlines page shows what is due everywhere. This is
// where the dates actually get created, because a statute has to be
// computed from a specific matter's state and date of incident.
//
// "Compute statutory dates" sends no inputs. The server reads State of
// Loss / Location of Loss and Date of Incident off the matter itself,
// using the same resolution the SOL endpoint has always used — which
// deliberately does NOT fall back to the client's address, since the
// statute follows where the incident happened and those differ whenever
// someone is hurt away from home. When it cannot determine either, the
// error says which custom field to set rather than guessing.
//
// Recomputing is safe and says so: anything completed or hand-edited is
// left alone and reported back, so correcting an incident date months
// later cannot resurrect a statute date over somebody's filing.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const KIND_LABELS = {
  sol: "Statute of limitations",
  notice_of_claim: "Notice of claim",
  discovery_response: "Discovery response",
  court_ordered: "Court ordered",
  statutory: "Statutory",
  contractual: "Contractual",
  internal: "Internal target",
  other: "Other",
};

const KINDS = Object.keys(KIND_LABELS);

function whenText(d) {
  const n = d.days_remaining;
  if (n === null || n === undefined) return "";
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} overdue`;
  if (n === 0) return "Due today";
  if (n === 1) return "Due tomorrow";
  return `${n} days`;
}

export function DeadlinesCard({ matterId, showNotification }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", due_date: "", kind: "internal" });
  const [noteDraft, setNoteDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(
        `/api/matters/${matterId}/deadlines?include_closed=${showClosed}`);
      setRows(d.data || []);
    } catch (e) {
      showNotification?.(`Deadlines failed to load: ${e.message}`, "error");
      setRows([]);
    }
  }, [matterId, showClosed, showNotification]);

  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`/api/matters/${matterId}/deadlines/compute`, {
        method: "POST", body: JSON.stringify({}),
      });
      const bits = [];
      if (r.created) bits.push(`${r.created} created`);
      if (r.updated) bits.push(`${r.updated} moved`);
      if (r.unchanged) bits.push(`${r.unchanged} unchanged`);
      if (r.protected) {
        bits.push(`${r.protected} left alone (completed or edited by hand)`);
      }
      showNotification?.(
        bits.length ? `Statutory dates: ${bits.join(", ")}.`
                    : "No statutory dates apply to this matter.",
        "success");
      await load();
    } catch (e) {
      // The server's message names the custom field to set, so it is
      // worth surfacing verbatim rather than replacing with a generic
      // failure.
      showNotification?.(e.message, "error");
    }
    setBusy(false);
  };

  const act = async (path, opts, okMsg) => {
    try {
      await apiFetch(path, opts);
      if (okMsg) showNotification?.(okMsg, "success");
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
  };

  const dismiss = async (d) => {
    const note = (noteDraft[d.id] || "").trim();
    if (!note) {
      showNotification?.(
        "Dismissing a deadline needs a reason — it is the only record of "
        + "why the firm decided it did not apply.", "warning");
      return;
    }
    await act(`/api/deadlines/${d.id}/not-applicable`,
      { method: "POST", body: JSON.stringify({ note }) });
    setNoteDraft((p) => ({ ...p, [d.id]: "" }));
  };

  const addManual = async () => {
    if (!draft.title.trim() || !draft.due_date) {
      showNotification?.("A deadline needs a title and a date.", "warning");
      return;
    }
    await act(`/api/matters/${matterId}/deadlines`, {
      method: "POST",
      body: JSON.stringify({
        title: draft.title.trim(),
        due_date: new Date(draft.due_date).toISOString(),
        kind: draft.kind,
      }),
    });
    setDraft({ title: "", due_date: "", kind: "internal" });
    setAdding(false);
  };

  if (rows === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", {
        style: { display: "flex", alignItems: "center", gap: "8px" },
      },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", {
          style: { fontSize: "13px", color: C.textMuted },
        }, "Loading deadlines…")));
  }

  const overdue = rows.filter((r) => r.is_past_due).length;

  return React.createElement("div", { style: cardStyle },
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
    },
      React.createElement("h2", { style: h2Style }, "Deadlines"),
      overdue > 0 && React.createElement("span", { style: tagStyle },
        `${overdue} overdue`),
      React.createElement("div", { style: { flex: 1 } }),
      React.createElement("button", {
        onClick: () => setShowClosed((v) => !v), style: quietBtn,
      }, showClosed ? "Hide closed" : "Show closed"),
    ),

    React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "6px 0 14px", lineHeight: 1.5 },
    }, rows.length === 0
        ? "Nothing tracked yet. Computing pulls the statute and any "
          + "notice-of-claim windows from this matter's state of loss and "
          + "date of incident."
        : "Statutory dates are computed from the state of loss and date of "
          + "incident. Recomputing leaves anything completed or hand-edited "
          + "untouched."),

    React.createElement("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap" } },
      React.createElement("button", {
        onClick: compute, disabled: busy, style: primaryBtn,
      }, busy ? "Computing…" : rows.length ? "Recompute statutory dates"
                                           : "Compute statutory dates"),
      React.createElement("button", {
        onClick: () => setAdding((v) => !v), style: quietBtn,
      }, "Add a deadline"),
    ),

    adding && React.createElement("div", {
      style: { marginTop: "13px", padding: "13px 15px", background: C.warmWhite,
               borderRadius: "8px", display: "flex", gap: "8px", flexWrap: "wrap" },
    },
      React.createElement("input", {
        value: draft.title,
        onChange: (e) => setDraft((p) => ({ ...p, title: e.target.value })),
        placeholder: "What is due", style: { ...inputStyle, flex: "2 1 200px" },
      }),
      React.createElement("input", {
        type: "date", value: draft.due_date,
        onChange: (e) => setDraft((p) => ({ ...p, due_date: e.target.value })),
        style: { ...inputStyle, flex: "1 1 140px" },
      }),
      React.createElement("select", {
        value: draft.kind,
        onChange: (e) => setDraft((p) => ({ ...p, kind: e.target.value })),
        style: { ...inputStyle, flex: "1 1 150px" },
      }, ...KINDS.map((k) =>
        React.createElement("option", { key: k, value: k }, KIND_LABELS[k]))),
      React.createElement("button", { onClick: addManual, style: primaryBtn }, "Add"),
    ),

    ...rows.map((d) => React.createElement("div", {
      key: d.id,
      style: {
        borderTop: `1px solid ${C.warmGray}`, padding: "13px 0",
        marginTop: "12px", opacity: d.status === "open" ? 1 : 0.55,
      },
    },
      React.createElement("div", {
        style: { display: "flex", gap: "10px", alignItems: "baseline", flexWrap: "wrap" },
      },
        React.createElement("span", {
          style: { fontSize: "13px", fontWeight: 700, color: C.navy, minWidth: "108px" },
        }, d.status === "open" ? whenText(d) : d.status.replace(/_/g, " ")),
        React.createElement("span", {
          style: { fontSize: "13px", fontWeight: 600, color: C.textDark },
        }, d.title),
        d.is_jurisdictional && React.createElement("span", { style: tagStyle },
          KIND_LABELS[d.kind] || d.kind),
        d.is_unowned && React.createElement("span", { style: tagStyle }, "Unassigned"),
        React.createElement("div", { style: { flex: 1 } }),
        React.createElement("span", {
          style: { fontSize: "12px", color: C.textMuted },
        }, d.due_date),
      ),

      d.basis && React.createElement("div", {
        style: { fontSize: "11px", color: C.textMuted, marginTop: "3px" },
      }, d.basis),

      d.resolution_note && React.createElement("div", {
        style: { fontSize: "12px", color: C.textDark, marginTop: "6px",
                 background: C.warmWhite, borderRadius: "6px", padding: "8px 10px" },
      }, d.resolution_note),

      d.status === "open" && React.createElement("div", {
        style: { display: "flex", gap: "8px", marginTop: "9px",
                 flexWrap: "wrap", alignItems: "center" },
      },
        React.createElement("button", {
          onClick: () => act(`/api/deadlines/${d.id}/complete`,
            { method: "POST", body: JSON.stringify({}) }, "Marked done."),
          style: primaryBtn,
        }, "Mark done"),
        React.createElement("input", {
          value: noteDraft[d.id] || "",
          onChange: (e) => setNoteDraft((p) => ({ ...p, [d.id]: e.target.value })),
          placeholder: "Reason, if this does not apply",
          style: { ...inputStyle, flex: "1 1 220px" },
        }),
        React.createElement("button", {
          onClick: () => dismiss(d), style: quietBtn,
        }, "Does not apply"),
      ),
    )),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const inputStyle = {
  padding: "7px 10px", borderRadius: "6px", border: "1px solid #E8E3DC",
  fontSize: "12px", fontFamily: "inherit", background: "#fff",
  boxSizing: "border-box", minWidth: 0,
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "7px 13px", fontSize: "12px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "7px 14px", fontSize: "12px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
const tagStyle = {
  fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.06em", color: "#6B6660",
  border: "1px solid #E8E3DC", borderRadius: "4px", padding: "2px 7px",
};
